import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { NameValueDto } from '@ig-core/interfaces/name-value-dto';
import { ReferenceCodeService } from '@app/admin/reference-code/reference-code.service';
import { ApplicationService } from '@app/applications/application.service';
import { LoanApplication, ApplicationAccessSpecifier } from '@app/applications/applications.model';
import { ReferenceCode } from '@app/admin/reference-code/reference-code.model';
import { ValidationService } from '../../../application-validators/validation.service'
import { MatSnackBar } from '@angular/material';
import { FolderFilesComposite } from '../../loanInformation/loan-folder-files/loan-folder-files.model';
import { FileUploadService } from '@app/utils/file-upload/file-upload.service';
import { IgFileService } from '@ig-core/form/igFile.service';
import { ConfirmationDialogService } from '@app/utils/confirmation-dialog/confirmation-dialog.service';
import * as fileSaver from 'file-saver';
import { ImagePreviewDialogService } from '@app/utils/image-preview-dialog/image-preview-dialog.service';
import { ControlValidators } from '@app/applications/application-validators/control-validators.directive';
import { Applicant } from '../../collateral/collateralDetails/collateral-details.model';
@Component({
  selector: 'eng-financial-info',
  templateUrl: 'financial-info.template.html',
  styleUrls: ['../../application-details.styles.scss', './financial-info.scss']
})
export class FinancialInfoComponent implements OnInit {

  @Input()
  application: LoanApplication;
  @Input()
  menuCode: string;

  // financial related variables
  financialAssessmentCompositeDto: any = {};
  financialSummary: any = {};
  financialInfoForm: FormGroup;
  isFormDisabled: boolean;

  allowAccess: boolean;
  menuItemAllowAccess: boolean;
  applicationAccessSpecifiers: ApplicationAccessSpecifier[];
  menuItemAccessSpecifier: ApplicationAccessSpecifier;

  businessExpenseRef: ReferenceCode[];
  assessmentTypes: ReferenceCode[];
  additionalIncomeRef: NameValueDto[];
  allBankStatements: any = []

  // business related variables
  ownershipTypes: NameValueDto[];
  businessNatures: NameValueDto[];
  businessAssessmentForm: FormGroup;
  businessOperationFiles: FolderFilesComposite;
  businessPlaceVerificationFiles: FolderFilesComposite;
  allFilesInFolder: FolderFilesComposite;

  hideOrShowFinancial: boolean;
  hideOrShowBusiness: boolean;
  applicants: Applicant[];
  roofTypes: NameValueDto[];

  constructor(private formBuilder: FormBuilder,
    private referenceCodeService: ReferenceCodeService,
    private applicationService: ApplicationService,
    private _snackbar: MatSnackBar,
    private validationService: ValidationService,
    private fileService: IgFileService,
    private fileUploadService: FileUploadService,
    private confirmationDialogService: ConfirmationDialogService,
    private imagePreviewDialogService: ImagePreviewDialogService) {
    this.financialInfoForm = this.formBuilder.group({
      monthlySales: [0],  //default value is 0
      monthlyIncome: [''],
      incomeAssessmentType: [''],
      affordableEmi: [''],
      totalLiability: [0],

      businessExpenses: this.formBuilder.array([]),
      additionalIncomes: this.formBuilder.array([])
    });

    this.businessAssessmentForm = this.formBuilder.group({
      natureOfBusiness: [''],
      currentStockLevels: [''],
      appearanceOfStock: [''],
      seasonalityInBusiness: [''],
      dailyTurnoverVolume: [''],
      marginPercent: [''],
      assessedMarginPercent: [''],
      monthlyProfit: [''],
      assetsPurchases: [''],
      remarks: [''],

      landmarkConfirmation: [''],
      distanceFromBranch: [''],
      yearsInBusiness: [''],
      ownershipType: [''],
      ownerName: [''],
      ownerPhoneNumber: [''],
      rentPaidAmount: [''],
      constructionType: [''],
      buildingArea: [''],
      assetsObserved: [''],
      verificationRemarks: [''],
      documentsObserved: [''],
    });
    this.allowAccess = this.applicationService.allowAccess;
  }

  ngOnInit() {
    this.isFormDisabled = true;
    this.hideOrShowFinancial = false;
    this.hideOrShowBusiness = false;
    this.fetchApplicants();
    this.applyValidatorsToFinancialDetails();
    this.getRefCodes();
    this.getMenuItemAccess();
  }

  fetchApplicants() {
    this.applicationService.getApplicants(this.application.uid).subscribe((response: any) => {
      if (response) {
        this.applicants = response;
      }
    })
  }

  applyValidatorsToFinancialDetails() {
    this.validationService.applyValidationRules(this.financialInfoForm, "FinancialAssessmentDetails").then((controlValidators) => {
    }).catch(() => {
    })
    if (this.hasBusinessParty()) {
      this.validationService.applyValidationRules(this.businessAssessmentForm, "BusinessAssessmentDetails").then((controlValidators) => {
      }).catch(() => {
      })
    }
  }

  getRefCodes() {
    this.referenceCodeService.requestRefCodesForClassfier('income_assessment_type').subscribe((response: any) => {
      this.assessmentTypes = response;
      this.referenceCodeService.requestRefCodesForClassfier('business_expense').subscribe((response: any) => {
        this.businessExpenseRef = response;
        this.referenceCodeService.getShortRefCodes('family_income').subscribe((response: any) => {
          this.additionalIncomeRef = response.family_income;
          this.fetchBankStatements();
        });
      });
    });
    this.referenceCodeService.getShortRefCodes('premise_ownership').subscribe((response: any) => {
      this.ownershipTypes = response.premise_ownership;
    });
    this.referenceCodeService.getShortRefCodes('business_nature').subscribe((response: any) => {
      this.businessNatures = response.business_nature;
    });

    this.referenceCodeService.getShortRefCodes('residence_roof_type').subscribe((response: any) => {
      this.roofTypes = response.residence_roof_type;
    });
  }

  fetchBankStatements() {
    this.applicationService.getBankStatements(this.application.uid).subscribe((response: any) => {
      this.allBankStatements = response.body;
      this.fetchFinancialAssessment();

    });
  }

  fetchFinancialAssessment() {
    this.applicationService.getFinancialAssessment(this.application.uid).subscribe((response: any) => {
      if (response.body && response.body.financialAssessment) {
        this.financialAssessmentCompositeDto = response.body;
        this.buildFinancialInfoForm(response.body.financialAssessment);
        if (this.hasBusinessParty()) {
          this.buildBusinessInfoForm(response.body.businessAssessment);
        }
      }
    });
  }

  get businessExpenses(): FormArray {
    return this.financialInfoForm.get('businessExpenses') as FormArray;
  }

  get additionalIncomes(): FormArray {
    return this.financialInfoForm.get('additionalIncomes') as FormArray;
  }

  buildFinancialInfoForm(financialAssessment?: any) {
    const incomeExpense = financialAssessment.incomeExpenseAssessment || {};

    const businessExpenseAssessment = incomeExpense.businessExpenseAssessment || [];
    const additionalIncome = incomeExpense.additionalIncome || [];

    while (this.businessExpenses.length) {
      this.businessExpenses.removeAt(0);
    }

    while (this.additionalIncomes.length) {
      this.additionalIncomes.removeAt(0);
    }

    if (this.allBankStatements && this.allBankStatements.businessIncome) {
      this.financialInfoForm.patchValue({
        monthlySales: this.allBankStatements.businessIncome.monthlySales
      })
    }

    if (incomeExpense.incomeAssessmentData) {
      this.financialInfoForm.patchValue({
        monthlyIncome: incomeExpense.incomeAssessmentData.monthlyIncome,
        incomeAssessmentType: incomeExpense.incomeAssessmentData.incomeAssessmentType,
        affordableEmi: incomeExpense.incomeAssessmentData.affordableEmi,
        totalLiability: incomeExpense.totalLiability,
      });
    }

    this.businessExpenseRef.forEach(ref => {
      const matchedExpense = businessExpenseAssessment.find(
        (e: any) => e.expenseType === ref.code
      );

      this.businessExpenses.push(
        this.formBuilder.group({
          expenseTypeCode: [ref.code],
          expenseTypeName: [ref.name],
          amount: [matchedExpense ? matchedExpense.amount : 0]
        })
      );
    });

    this.additionalIncomeRef.forEach(ref => {
      const matchedIncome = additionalIncome.find(
        (i: any) => i.incomeType === ref.code
      );

      this.additionalIncomes.push(
        this.formBuilder.group({
          incomeTypeCode: [ref.code],
          incomeTypeName: [ref.name],
          amount: [matchedIncome ? matchedIncome.amount : 0]
        })
      );
    });
    this.computeSummary();
    this.financialInfoForm.disable();
  }

  buildBusinessInfoForm(businessAssessment?: any) {
    const operationVerification =
      businessAssessment.businessOperationVerification || {};

    const placeVerification =
      businessAssessment.businessPlaceVerification || {};

    const operationData =
      operationVerification.businessOperationVerificationAssessmentData || {};

    const placeData =
      placeVerification.businessPlaceVerificationAssessmentData || {};

    this.businessAssessmentForm.patchValue({
      // BUSINESS OPERATION VERIFICATION
      natureOfBusiness: operationData.natureOfBusiness,
      currentStockLevels: operationData.currentStockLevels,
      appearanceOfStock: operationData.appearanceOfStock,
      seasonalityInBusiness: operationData.seasonalityInBusiness,
      dailyTurnoverVolume: operationData.dailyTurnoverVolume,
      marginPercent: operationData.marginPercent,
      assessedMarginPercent: operationData.assessedMarginPercent,
      monthlyProfit: operationData.monthlyProfit,
      assetsPurchases: operationData.assetsPurchases,
      remarks: operationData.remarks,

      // BUSINESS PLACE VERIFICATION
      landmarkConfirmation: placeData.landmarkConfirmation,
      distanceFromBranch: placeData.distanceFromBranch,
      yearsInBusiness: placeData.yearsInBusiness,
      ownershipType: placeData.ownershipType,
      ownerName: placeData.ownerName,
      ownerPhoneNumber: placeData.ownerPhoneNumber,
      rentPaidAmount: placeData.rentPaidAmount,
      constructionType: placeData.constructionType,
      buildingArea: placeData.buildingArea,
      assetsObserved: placeData.assetsObserved,
      verificationRemarks: placeData.remarks,
      documentsObserved: placeData.documentsObserved
    });
    this.businessAssessmentForm.disable();
    this.getSelectedFolderFiles(operationVerification.fileFolderUid, 'BUSINESS_OPERATION');
    this.getSelectedFolderFiles(placeVerification.fileFolderUid, 'BUSINESS_PLACE');
  }

  getSelectedFolderFiles(folderUid, fileType: 'BUSINESS_OPERATION' | 'BUSINESS_PLACE') {
    this.applicationService.getAllFilesInApplicationFolder(this.application.uid, folderUid).subscribe((response: any) => {
      if (response) {
        this.allFilesInFolder = response.body;
        this.allFilesInFolder.fileInfo.forEach(element => {
          if (element.fileSize) {
            element["convertedFileSize"] = this.applicationService.bytesToSize(element.fileSize)
          }
        });
        if (fileType === 'BUSINESS_OPERATION') {
          this.businessOperationFiles = this.allFilesInFolder;
        } else {
          this.businessPlaceVerificationFiles = this.allFilesInFolder;
        }
      }
    });
  }

  saveFinancialDetails() {
    this.validationService.markFormGroupTouched(this.financialInfoForm)
    this.validationService.markFormGroupTouched(this.businessAssessmentForm);

    if (this.isFormDisabled) {
      return;
    }

    if (!this.financialInfoForm.valid && this.hasBusinessParty() && !this.businessAssessmentForm.valid) {
      this._snackbar.open("Please fill in financial and business details.", "Close", {
        duration: 4000,
      });
      return;
    }

    if (!this.financialInfoForm.valid) {
      this._snackbar.open("Please fill in financial details.", "Close", {
        duration: 4000,
      });
      return;
    }

    if (this.hasBusinessParty() && !this.businessAssessmentForm.valid) {
      this._snackbar.open("Please fill in business details.", "Close", {
        duration: 4000,
      });
      return;
    }

    const financialFormValue = this.financialInfoForm.getRawValue();

    if (!this.financialAssessmentCompositeDto) {
      this.financialAssessmentCompositeDto = {};
    }

    if (!this.financialAssessmentCompositeDto.financialAssessment) {
      this.financialAssessmentCompositeDto.financialAssessment = {};
    }

    const financialAssessment = this.financialAssessmentCompositeDto.financialAssessment;

    if (!financialAssessment.uid) {
      financialAssessment.category = 'FINANCIAL';
      financialAssessment.contextUid = this.application.uid;
      financialAssessment.contextType = 'application';
    }

    financialAssessment.incomeExpenseAssessment = {
      incomeAssessmentData: {
        monthlyIncome: financialFormValue.monthlyIncome,
        incomeAssessmentType: financialFormValue.incomeAssessmentType,
        affordableEmi: financialFormValue.affordableEmi
      },

      businessExpenseAssessment: financialFormValue.businessExpenses.map((exp: any) => ({
        expenseType: exp.expenseTypeCode,
        amount: exp.amount
      })),

      additionalIncome: financialFormValue.additionalIncomes.map((inc: any) => ({
        incomeType: inc.incomeTypeCode,
        amount: inc.amount
      })),

      assessmentFinancialSummary: {
        disposableIncome: this.financialSummary.disposableIncome,
        emiAsPerApplicableFoir: this.financialSummary.emiAsPerApplicableFoir,
        affordableEmi: this.financialSummary.affordableEmi,
        finalEmiEligibility: this.financialSummary.finalEmiEligibility
      },
      totalLiability: financialFormValue.totalLiability

    };

    // if business is not captured then business assessment should not create hence we are sending businessAssessment as null value
    const payload: any = {
      financialAssessment: financialAssessment,
      businessAssessment: null
    };

    if (this.hasBusinessParty()) {
      const businessFormValue = this.businessAssessmentForm.getRawValue();

      if (!this.financialAssessmentCompositeDto) {
        this.financialAssessmentCompositeDto = {};
      }

      if (!this.financialAssessmentCompositeDto.businessAssessment) {
        this.financialAssessmentCompositeDto.businessAssessment = {};
      }

      const businessAssessment = this.financialAssessmentCompositeDto.businessAssessment;

      if (!businessAssessment.businessOperationVerification) {
        businessAssessment.businessOperationVerification = {};
      }

      const operationVerification = businessAssessment.businessOperationVerification;

      // Set only for CREATE
      if (!operationVerification.uid) {
        operationVerification.category = 'BUSINESS_OPERATION_VERIFICATION';
        operationVerification.contextUid = this.application.uid;
        operationVerification.contextType = 'application';
      }

      operationVerification.businessOperationVerificationAssessmentData = {
        natureOfBusiness: businessFormValue.natureOfBusiness,
        currentStockLevels: businessFormValue.currentStockLevels,
        appearanceOfStock: businessFormValue.appearanceOfStock,
        seasonalityInBusiness: businessFormValue.seasonalityInBusiness,
        dailyTurnoverVolume: businessFormValue.dailyTurnoverVolume,
        marginPercent: businessFormValue.marginPercent,
        assessedMarginPercent: businessFormValue.assessedMarginPercent,
        monthlyProfit: businessFormValue.monthlyProfit,
        assetsPurchases: businessFormValue.assetsPurchases,
        remarks: businessFormValue.remarks
      };

      if (!businessAssessment.businessPlaceVerification) {
        businessAssessment.businessPlaceVerification = {};
      }

      const placeVerification = businessAssessment.businessPlaceVerification;

      // Set only for CREATE
      if (!placeVerification.uid) {
        placeVerification.category = 'BUSINESS_PLACE_VERIFICATION';
        placeVerification.contextUid = this.application.uid;
        placeVerification.contextType = 'application';
      }

      placeVerification.businessPlaceVerificationAssessmentData = {
        landmarkConfirmation: businessFormValue.landmarkConfirmation,
        distanceFromBranch: businessFormValue.distanceFromBranch,
        yearsInBusiness: businessFormValue.yearsInBusiness,
        ownershipType: businessFormValue.ownershipType,
        ownerName: businessFormValue.ownerName,
        ownerPhoneNumber: businessFormValue.ownerPhoneNumber,
        rentPaidAmount: businessFormValue.rentPaidAmount,
        constructionType: businessFormValue.constructionType,
        buildingArea: businessFormValue.buildingArea,
        assetsObserved: businessFormValue.assetsObserved,
        remarks: businessFormValue.verificationRemarks,
        documentsObserved: businessFormValue.documentsObserved
      };
      payload.businessAssessment = businessAssessment;
    }

    this.applicationService.saveFinancialAssessment(this.application.uid, payload).toPromise().then(
      (_success) => {
        this._snackbar.open("Bank balance data updated successfully", "Close", {
          duration: 2000,
        });
        this.isFormDisabled = true;
        this.buildFinancialInfoForm(_success.body.financialAssessment);
        if (this.hasBusinessParty()) {
          this.buildBusinessInfoForm(_success.body.businessAssessment);
        }
      }, (failure) => {
        let errormesg = []
        errormesg.push(failure)
        this.applicationService.displayErrorMessages(errormesg);
      }
    );
  }

  computeSummary() {
    const formValue = this.financialInfoForm.getRawValue();

    const payload = {
      incomeAssessmentData: {
        monthlyIncome: formValue.monthlyIncome,
        incomeAssessmentType: formValue.incomeAssessmentType,
        affordableEmi: formValue.affordableEmi
      },

      businessExpenseAssessment: formValue.businessExpenses.map((exp: any) => ({
        expenseType: exp.expenseTypeCode,
        amount: exp.amount
      })),

      additionalIncome: formValue.additionalIncomes.map((inc: any) => ({
        incomeType: inc.incomeTypeCode,
        amount: inc.amount
      })),

      totalLiability: formValue.totalLiability
    };

    this.applicationService.calculateSummary(this.application.uid, payload).subscribe((response: any) => {
      if (response.body) {
        this.financialSummary = response.body;
      }
    });
  }

  enableDisableForm() {
    this.financialInfoForm.enable();
    // by default after form is build then businessAssessment form is disabled.
    // If 'business' is NOT present in dummyParties, then business is captured 
    // so we need to enable the form to capture the data
    if (this.hasBusinessParty()) {
      this.businessAssessmentForm.enable();
      this.onOwnershipTypeChange(this.businessAssessmentForm.value.ownershipType);
    }
    this.financialInfoForm.get('monthlySales').disable();
    this.businessExpenses.controls.forEach((group: any) => {
      group.get('expenseTypeName').disable();
    });
    this.additionalIncomes.controls.forEach((group: any) => {
      group.get('incomeTypeName').disable();
    });
    this.isFormDisabled = false;
  }

  cancelForm() {
    this.isFormDisabled = true;
    this.fetchFinancialAssessment();
    this.financialInfoForm.disable();
  }

  //this function will get either this menu item  is editable or readonly 
  getMenuItemAccess() {
    this.applicationService.getApplicationAccessModifiers(this.application.uid)
      .subscribe((response) => {
        this.applicationAccessSpecifiers = response.body;
        this.menuItemAccessSpecifier = this.applicationAccessSpecifiers
          .find(accessSpecifier => accessSpecifier.category === this.menuCode);
        if (this.menuItemAccessSpecifier) {
          this.menuItemAllowAccess = this.menuItemAccessSpecifier.allowAccess;
        }
      });
  }

  openImageFileUploadDialog(type: 'BUSINESS_OPERATION' | 'BUSINESS_PLACE') {
    const folderUid =
      type === 'BUSINESS_OPERATION'
        ? this.financialAssessmentCompositeDto.businessAssessment.businessOperationVerification.fileFolderUid
        : this.financialAssessmentCompositeDto.businessAssessment.businessPlaceVerification.fileFolderUid;

    const files =
      type === 'BUSINESS_PLACE'
        ? this.businessOperationFiles.fileInfo
        : this.businessPlaceVerificationFiles.fileInfo;
    this.fileUploadService.open({ "uploadType": "multiple", "folderUid": folderUid, "applicationUid": this.application.uid, "files": files })
    this.fileUploadService.confirmed().subscribe(data => {
      this.getSelectedFolderFiles(data.folderUid, type)
    })
  }

  openfileDeleteConfirmation(selectedFile, fileType: 'BUSINESS_OPERATION' | 'BUSINESS_PLACE') {
    this.confirmationDialogService.open({ "btnCancelText": "Cancel", "btnConfirmText": "Delete", "message": "Are you sure you want to delete this File?" })
    this.confirmationDialogService.confirmed().subscribe(data => {
      if (data) {
        this.deleteFile(selectedFile, fileType)
      }
    })
  }

  deleteFile(selectedFile, fileType) {
    this.applicationService.fileDelete(this.application.uid, selectedFile.uid).subscribe((response) => {
      if (response.body.status == "success") {
        this._snackbar.open("File deleted successfully", "Close", {
          duration: 2000,
        });
        if (fileType === 'BUSINESS_OPERATION') {
          this.getSelectedFolderFiles(
            this.financialAssessmentCompositeDto.businessAssessment.businessOperationVerification.fileFolderUid,
            'BUSINESS_OPERATION'
          );
        } else {
          this.getSelectedFolderFiles(
            this.financialAssessmentCompositeDto.businessAssessment.businessPlaceVerification.fileFolderUid,
            'BUSINESS_PLACE'
          );
        }
      } else {
        let errormesg = []
        errormesg.push("File deleted failed")
        errormesg.push(response.body.message)
        this.applicationService.displayErrorMessages(errormesg);
      }
    }, (failure) => {
      let errormesg = []
      errormesg.push("File deleted failed")
      errormesg.push(failure)
      this.applicationService.displayErrorMessages(errormesg);
    });
  }

  downloadFile(file) {
    this.applicationService.downloadFileFromApplicationFolder(this.application.uid, file.folderUid, file.fileId).subscribe(image => {
      const blob = new Blob([image.data], { type: file.type });
      fileSaver.saveAs(blob, file.fileName);
      this._snackbar.open("File has been downloaded successfully", "Close", {
        duration: 5000,
      });
    })
  }

  openContentElement(fileType) {
    this.imagePreviewDialogService.open({ "images": fileType === 'BUSINESS_OPERATION' ? this.businessOperationFiles : this.businessPlaceVerificationFiles })
  }

  viewPdf(file) {
    this.fileService.fileStreamById(file.fileId, "false").subscribe(res => {
      window.open(res.data, '_blank');
    })
  }

  hideOrShowDetails(attribute: string) {
    if (attribute === 'financial') {
      this.hideOrShowFinancial = !this.hideOrShowFinancial;
    } else if (attribute === 'business') {
      this.hideOrShowBusiness = !this.hideOrShowBusiness;
    }
  }

  onOwnershipTypeChange(value?: string, isDropdownChange: boolean = false) {
    const control = this.businessAssessmentForm.controls.rentPaidAmount;
    // Clear the value when dropdown is changed by user
    if (isDropdownChange) {
      control.setValue(null);
      this.businessAssessmentForm.controls.ownerName.setValue(null);
      this.businessAssessmentForm.controls.ownerPhoneNumber.setValue(null);
    }
    if (value !== 'RENTD') {
      this.businessAssessmentForm.controls['rentPaidAmount']
        .setValidators([ControlValidators.numberValidator("Rent paid should be a number")]);
    } else {
      this.businessAssessmentForm.controls['rentPaidAmount']
        .setValidators([ControlValidators.requiredValidator("Rent paid amount is required"),
        ControlValidators.numberValidator("Rent paid should be a number")]);
    }
    control.updateValueAndValidity();
  }
  onOwnerSelected(selectedApplicantUid: any) {
    let selectedApplicant = this.applicants.find(applicant => applicant.uid === selectedApplicantUid);
    if (selectedApplicant) {
      this.businessAssessmentForm.patchValue({
        ownerPhoneNumber: selectedApplicant.primaryPhone
      });
    }
  }

  hasBusinessParty(): boolean {
    return !this.application.dummyParties.some(p => p.trim().toLowerCase() === 'business');
  }
}